import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { canCancelBooking, cancellationPhase } from '@/lib/bookingCancel';

export default function CustomerCancelBooking({ booking, onCancelled }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reasonCode, setReasonCode] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [loading, setLoading] = useState(false);

  const allowed = canCancelBooking(booking);
  const phase = cancellationPhase(booking);

  useEffect(() => {
    if (!open || !allowed) return;
    api.get(`/bookings/${booking.id}/cancellation-preview`)
      .then(({ data }) => {
        if (!data.can_cancel) {
          setPreview(null);
          return;
        }
        setPreview(data.preview);
        setReasons(data.reasons || []);
      })
      .catch(() => toast.error('Could not load cancellation preview'));
  }, [open, booking?.id, allowed]);

  if (!allowed) return null;

  const confirm = async () => {
    const reason = reasonCode === 'other' ? otherReason.trim() : reasonCode;
    if (!reason) {
      toast.error('Please select or enter a reason');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/bookings/${booking.id}/cancel`, {
        reason,
        reason_code: reasonCode === 'other' ? 'other' : reasonCode,
      });
      toast.success('Booking cancelled');
      setOpen(false);
      onCancelled?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancellation failed');
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="outline"
        className="w-full border-red-200 text-red-700"
        data-testid="customer-cancel-booking-btn"
        onClick={() => setOpen(true)}
      >
        Cancel Booking
      </Button>
    );
  }

  return (
    <div className="resqly-card p-4 space-y-3 border border-red-100" data-testid="customer-cancel-dialog">
      <div className="font-semibold text-red-800">Cancel Booking</div>
      {phase === 'no_charge' && (
        <p className="text-sm text-slate-600" data-testid="cancel-no-charge">No cancellation charge.</p>
      )}
      {phase === 'travel_charge' && preview && (
        <div className="text-sm space-y-1" data-testid="cancel-travel-preview">
          <p>{preview.message}</p>
          <p>Refund amount: ₹{Number(preview.refund_amount || 0).toFixed(2)}</p>
        </div>
      )}
      <div className="space-y-2">
        {reasons.map((r) => (
          <label key={r} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="cancel-reason"
              value={r}
              checked={reasonCode === r}
              onChange={() => setReasonCode(r)}
            />
            {r}
          </label>
        ))}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="cancel-reason"
            value="other"
            checked={reasonCode === 'other'}
            onChange={() => setReasonCode('other')}
          />
          Other
        </label>
        {reasonCode === 'other' && (
          <input
            className="w-full border rounded p-2 text-sm"
            placeholder="Reason"
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
            data-testid="cancel-other-reason"
          />
        )}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Back</Button>
        <Button
          className="flex-1 bg-red-700 hover:bg-red-800"
          disabled={loading}
          data-testid="confirm-customer-cancel"
          onClick={confirm}
        >
          Confirm Cancellation
        </Button>
      </div>
    </div>
  );
}
